import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  getDevicePlaneDlq,
  getDevicePlaneOverview,
  getDevicePlaneRegistrySync,
} from '../api/devicePlane.js';

function agoLabel(sec: number | null): string {
  if (sec === null) return '—';
  if (sec < 90) return `${sec} s`;
  if (sec < 5400) return `${Math.round(sec / 60)} m`;
  if (sec < 129600) return `${Math.round(sec / 3600)} h`;
  return `${Math.round(sec / 86400)} d`;
}

export function DevicePlanePipeline() {
  const { data: ov } = useQuery({
    queryKey: ['dp-overview'],
    queryFn: getDevicePlaneOverview,
    refetchInterval: 5000,
  });
  const { data: dlq } = useQuery({
    queryKey: ['dp-dlq'],
    queryFn: () => getDevicePlaneDlq(50),
    refetchInterval: 15000,
  });
  const { data: sync } = useQuery({
    queryKey: ['dp-registry-sync'],
    queryFn: getDevicePlaneRegistrySync,
    refetchInterval: 30000,
  });

  const streams = [
    { key: 'deviceEvents' as const, label: 'events:device', dir: 'middleware → backend' },
    { key: 'registryEvents' as const, label: 'events:registry', dir: 'backend → middleware' },
  ];

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-ink">Device Plane — Pipeline</h1>
        <p className="text-sm text-muted mt-0.5">
          The backend↔middleware event pipeline: Redis Streams health, dead-lettered events and registry-mirror sync.
        </p>
      </div>

      {/* Streams table */}
      <div className="bg-surface border border-white/10 rounded-xl p-4 overflow-x-auto">
        <p className="text-sm font-semibold text-ink2 mb-2">Redis Streams</p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted border-b border-grid">
              <th className="py-1.5">Stream</th>
              <th className="py-1.5">Direction</th>
              <th className="py-1.5">Group</th>
              <th className="py-1.5 text-right">Length</th>
              <th className="py-1.5 text-right">Pending</th>
              <th className="py-1.5 text-right">Lag</th>
              <th className="py-1.5 text-right">DLQ</th>
            </tr>
          </thead>
          <tbody>
            {streams.map((s) => {
              const info = ov?.streams[s.key];
              const g = info?.groups[0];
              return (
                <tr key={s.key} className="border-b border-grid">
                  <td className="py-1.5 font-mono text-xs text-ink2">{s.label}</td>
                  <td className="py-1.5 text-xs text-muted">{s.dir}</td>
                  <td className="py-1.5 font-mono text-xs text-muted">{g?.name ?? '—'}</td>
                  <td className="py-1.5 text-right tabular-nums text-ink">{info?.length ?? '—'}</td>
                  <td className="py-1.5 text-right tabular-nums text-ink2">{g?.pending ?? '—'}</td>
                  <td className="py-1.5 text-right tabular-nums text-ink2">{g?.lag ?? '—'}</td>
                  <td className={`py-1.5 text-right tabular-nums font-semibold ${
                    (info?.dlqLength ?? 0) > 0 ? 'text-warnink' : 'text-ink2'
                  }`}>{info?.dlqLength ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* DLQ inspector */}
        <div className="bg-surface border border-white/10 rounded-xl p-4 overflow-x-auto">
          <p className="text-sm font-semibold text-ink2 mb-1">Dead-letter inspector</p>
          <p className="text-xs text-muted mb-2">Events that failed to process — with the reason and the poisoned payload.</p>
          {(dlq?.items ?? []).length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted border-b border-grid">
                  <th className="py-1.5">Entry</th>
                  <th className="py-1.5">Reason</th>
                  <th className="py-1.5">Payload</th>
                </tr>
              </thead>
              <tbody>
                {(dlq?.items ?? []).map((d) => (
                  <tr key={`${d.stream}-${d.id}`} className="border-b border-grid align-top">
                    <td className="py-1.5 font-mono text-[11px] text-muted whitespace-nowrap">
                      {d.stream.replace('events:', '').replace(':dlq', '')}<br />{d.id}
                    </td>
                    <td className="py-1.5">
                      <span className="inline-block text-[11px] font-medium rounded-full px-2 py-0.5 bg-bad/15 text-badink">
                        {d.reason ?? 'unknown'}
                      </span>
                    </td>
                    <td className="py-1.5">
                      <details>
                        <summary className="text-xs text-accent cursor-pointer">payload</summary>
                        <pre className="mt-1 bg-page border border-grid rounded-lg p-2 text-[11px] text-ink2 overflow-x-auto max-w-72 font-mono">
                          {typeof d.payload === 'string' ? d.payload : JSON.stringify(d.payload, null, 1)}
                        </pre>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-muted py-6 text-center">Both dead-letter queues are empty 🎉</p>
          )}
        </div>

        {/* Registry sync attention list */}
        <div className="bg-surface border border-white/10 rounded-xl p-4 overflow-x-auto">
          <p className="text-sm font-semibold text-ink2 mb-1">Registry sync — attention list</p>
          <p className="text-xs text-muted mb-2">Devices never seen online since registration, or with a stale mirror row (&gt;24 h).</p>
          {(sync?.items ?? []).length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted border-b border-grid">
                  <th className="py-1.5">Serial</th>
                  <th className="py-1.5">Lifecycle</th>
                  <th className="py-1.5 text-right">Synced</th>
                  <th className="py-1.5">Note</th>
                </tr>
              </thead>
              <tbody>
                {(sync?.items ?? []).map((r) => (
                  <tr key={r.deviceId} className="border-b border-grid">
                    <td className="py-1.5 font-mono text-xs">
                      <Link to={`/device-plane/fleet/${r.deviceId}`} className="text-accent hover:underline">
                        {r.serialNumber}
                      </Link>
                    </td>
                    <td className="py-1.5 text-xs text-ink2">{r.lifecycleState}</td>
                    <td className="py-1.5 text-right tabular-nums text-xs text-muted">{agoLabel(r.updatedAgoSec)} ago</td>
                    <td className="py-1.5">
                      {r.firstOnlinePending && (
                        <span className="inline-block text-[11px] font-medium rounded-full px-2 py-0.5 bg-warn/15 text-warnink">
                          never online
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-muted py-6 text-center">Every mirror row is fresh and every registered device has come online 🎉</p>
          )}
        </div>
      </div>

      {/* Honest placeholders for the two demo widgets that need new logging */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="border border-dashed border-warn rounded-xl p-4 text-sm text-ink2 bg-surface">
          <b className="text-ink">Cron sweep activity</b> — the timeout and queue-expiry sweepers run every minute and
          log their counts to the middleware console, but nothing is stored per run yet. A small
          <span className="font-mono text-xs"> cron_runs</span> table would make this widget live.
        </div>
        <div className="border border-dashed border-warn rounded-xl p-4 text-sm text-ink2 bg-surface">
          <b className="text-ink">Command rejections</b> — DEVICE_UNKNOWN / CMD_FAMILY_INCOMPATIBLE / VALIDATION_FAILED
          are thrown at dispatch and not recorded anywhere. A rejection counter table in the command router would make
          these invisible failures visible here.
        </div>
      </div>
    </div>
  );
}
